/**
 * LeadDeletionModal — Administrator-only permanent Lead deletion UI.
 *
 * Flow:
 *  1. On open: run preflight scan and display results (blocks / cascade counts).
 *  2. If blocked: show what's blocking, no delete available.
 *  3. If safe: show reason dropdown, optional explanation, typed confirmation.
 *  4. On confirm: call DELETE endpoint and report outcome.
 */

import { useState, useEffect, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASONS = [
  { value: "duplicate_created_in_error", label: "Duplicate created in error" },
  { value: "created_in_error", label: "Created in error" },
  { value: "test_qa_record", label: "Test / QA record" },
  { value: "spam_junk", label: "Spam or junk" },
  { value: "bad_import", label: "Bad import" },
  { value: "wrong_person", label: "Wrong person" },
  { value: "other", label: "Other (explain below)" },
];

const CASCADE_LABELS = {
  lead_profiles: "Lead profile",
  client_tax_profiles: "Tax profile",
  contact_change_records: "Change records",
  proposed_record_updates: "Proposed updates",
  conversations: "Conversations",
  messages: "Messages",
  historical_conversations: "Historical conversations",
  penny_messages: "Penny messages",
  client_notes: "Notes",
  notifications: "Notifications",
  information_requests: "Information requests",
  portal_users: "Portal access",
  rebook_tokens: "Rebook tokens",
  appointment_records: "Appointment records",
  appointments: "Appointments",
  meeting_transcripts: "Meeting transcripts",
  meeting_intake_commits: "Meeting intake commits",
  case_analyses: "Case analyses",
  consultation_summaries: "Consultation summaries",
  allison_card_notes: "Allison card notes",
  documents: "Documents",
  document_requests: "Document requests",
  document_requirement_matches: "Document requirement matches",
  document_classification_records: "Document classifications",
  document_intelligence_sessions: "Document intelligence sessions",
  organizers: "Organizers",
  str_completions: "STR completions",
  str_assignments: "STR assignments",
  str_assumptions: "STR assumptions",
  str_review_runs: "STR review runs",
  str_review_reminders: "STR review reminders",
  strategic_review_bases: "STR base records",
  strategic_review_sessions: "STR sessions",
  strategic_review_reports: "STR reports",
  strategic_review_report_jobs: "STR report jobs",
  strategic_review_reasonings: "STR reasonings",
  strategic_review_reasoning_jobs: "STR reasoning jobs",
  strategic_review_publications: "STR publications",
  tasks: "Tasks",
  automation_shadow_log: "Automation shadow log",
  dependency_eval_runs: "Dependency eval runs",
  workflow_states: "Workflow states",
  workflow_states_archive: "Archived workflow states",
  timeline_events: "Timeline events",
  proposals: "Proposals",
  estimates_cascade: "Estimates (eligible)",
  engagements_cascade: "Engagements (eligible)",
  invoices_cascade: "Invoices (eligible)",
};

const BLOCK_ICONS = {
  payment: Icons.CreditCard,
  receipt: Icons.Receipt,
  paid_invoice: Icons.FileCheck,
  blocked_estimate: Icons.FileWarning,
  active_engagement: Icons.FileSignature,
};

export default function LeadDeletionModal({ clientId, clientNumber, clientName, onClose, onSuccess }) {
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const expectedConfirmation = `DELETE ${clientNumber}`;

  const runPreflight = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crm/leads/${clientId}/deletion-preflight`);
      setPreflight(data);
    } catch (err) {
      toast.error("Could not run deletion preflight check");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [clientId, onClose]);

  useEffect(() => { runPreflight(); }, [runPreflight]);

  const isConfirmed =
    confirmation.trim() === expectedConfirmation &&
    reason !== "" &&
    (reason !== "other" || detail.trim().length > 0);

  const handleDelete = async () => {
    if (!isConfirmed) return;
    setSubmitting(true);
    try {
      const { data } = await api.delete(`/crm/leads/${clientId}/permanent`, {
        data: { reason, confirmation: confirmation.trim(), detail: detail.trim() || undefined },
      });
      toast.success(`Lead ${clientNumber} permanently deleted`);
      onSuccess?.(data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Deletion failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const cascadeTotal = preflight
    ? Object.values(preflight.cascade_counts || {}).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="lead-delete-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Icons.Trash2 className="h-5 w-5" />
            Delete Lead Permanently
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 py-6" data-testid="lead-delete-preflight-loading">
            <Icons.Loader2 className="h-4 w-4 animate-spin" />
            Running dependency scan…
          </div>
        )}

        {!loading && preflight && (
          <div className="space-y-5">
            {/* Identity row */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3" data-testid="lead-delete-identity">
              <p className="text-sm font-medium text-slate-800">{clientName}</p>
              <p className="text-xs text-slate-500">{clientNumber} · lifecycle: lead</p>
            </div>

            {/* Integrity blocks */}
            {preflight.blocks && preflight.blocks.length > 0 && (
              <div
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-2"
                data-testid="lead-delete-blocks"
              >
                <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                  <Icons.ShieldX className="h-4 w-4" />
                  Deletion blocked — financial records present
                </p>
                <ul className="space-y-1">
                  {preflight.blocks.map((b, i) => {
                    const BlockIcon = BLOCK_ICONS[b.type] || Icons.AlertCircle;
                    return (
                      <li key={i} className="flex items-center gap-2 text-sm text-red-600" data-testid={`lead-delete-block-${b.type}`}>
                        <BlockIcon className="h-3.5 w-3.5 shrink-0" />
                        {b.label}
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-red-500 mt-1">
                  Financial records cannot be deleted. This Lead cannot be purged while these records exist.
                </p>
              </div>
            )}

            {/* Cascade summary */}
            {preflight.is_safe_to_delete && (
              <div className="space-y-3">
                {cascadeTotal > 0 ? (
                  <div
                    className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
                    data-testid="lead-delete-cascade-summary"
                  >
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-2">
                      <Icons.AlertTriangle className="h-4 w-4" />
                      {cascadeTotal} dependent records will be permanently deleted
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 max-h-36 overflow-y-auto">
                      {Object.entries(preflight.cascade_counts || {}).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between text-xs text-amber-700">
                          <span>{CASCADE_LABELS[k] || k}</span>
                          <span className="font-medium">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 flex items-center gap-2"
                    data-testid="lead-delete-no-deps"
                  >
                    <Icons.CheckCircle2 className="h-4 w-4 shrink-0" />
                    No dependent records found — this is an empty Lead.
                  </div>
                )}

                {/* Reason */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Reason for deletion <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                    data-testid="lead-delete-reason-select"
                  >
                    <option value="">— Select a reason —</option>
                    {REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {reason === "other" && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700">
                      Explanation <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={detail}
                      onChange={(e) => setDetail(e.target.value)}
                      rows={2}
                      placeholder="Brief explanation required…"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                      data-testid="lead-delete-detail-input"
                    />
                  </div>
                )}

                {/* Typed confirmation */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Type <code className="bg-slate-100 rounded px-1 text-red-700 text-xs font-mono">{expectedConfirmation}</code> to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder={expectedConfirmation}
                    className={`w-full rounded-lg border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 ${
                      confirmation && confirmation.trim() !== expectedConfirmation
                        ? "border-red-300 focus:ring-red-300"
                        : "border-slate-300 focus:ring-red-300"
                    }`}
                    data-testid="lead-delete-confirmation-input"
                  />
                  {confirmation && confirmation.trim() !== expectedConfirmation && (
                    <p className="text-xs text-red-500" data-testid="lead-delete-confirmation-mismatch">
                      Text does not match. Type exactly: {expectedConfirmation}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="lead-delete-cancel-btn"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={!isConfirmed || submitting}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isConfirmed && !submitting
                        ? "bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-400"
                        : "bg-slate-200 text-slate-400 cursor-not-allowed"
                    }`}
                    data-testid="lead-delete-confirm-btn"
                  >
                    {submitting ? (
                      <><Icons.Loader2 className="h-4 w-4 animate-spin" /> Deleting…</>
                    ) : (
                      <><Icons.Trash2 className="h-4 w-4" /> Delete Permanently</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Blocked state — no delete UI */}
            {!preflight.is_safe_to_delete && preflight.eligible === false && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="lead-delete-not-eligible">
                <p className="font-medium text-slate-700 mb-1">Not eligible for deletion</p>
                <p>{preflight.message || "This contact is not a Lead."}</p>
              </div>
            )}

            {/* Blocked state — has integrity blocks but was a Lead */}
            {preflight.eligible && !preflight.is_safe_to_delete && (
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="lead-delete-blocked-close-btn"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
